import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "@/reducers/types";
import { FreeDrawText } from "@/sx-layout/components/plotmap/components/freeDraw/models";
import { Rectangle, UserRole, UserRoles } from "@/sx-layout/components/plotmap/models";
import { useTerm } from "@/sx-layout/hooks";
import { useLoginUser } from "@/sx-layout/hooks/useLoginUser";

export const useSelectedText = () => {
  const [freeDrawText, setFreeDrawText] = useState<FreeDrawText>();
  const [rect, setRect] = useState<Rectangle>();

  // 権限制御
  const layoutDate = useSelector<RootState, Date>((state) => state.plotmap.layoutDate);
  const { isPast } = useTerm(layoutDate);
  const userRole = useSelector<RootState, UserRoles>((state) => state.layoutApp.layoutMasters.user_role);
  const { loginUser } = useLoginUser();
  const isEditable = useMemo(() => {
    const isAdmin = userRole === UserRole.MASTER_ROLE;
    const isUserAndCompany =
      userRole === UserRole.USER_ROLE && freeDrawText && freeDrawText.company_id === loginUser?.company_id;

    return !isPast && (isAdmin || isUserAndCompany);
  }, [freeDrawText, loginUser?.company_id, userRole, isPast]);

  const start = useCallback((text: FreeDrawText, rect: Rectangle) => {
    setFreeDrawText(text);
    setRect(rect);
  }, []);

  const end = useCallback(() => {
    setFreeDrawText(null);
    setRect(null);
  }, []);

  const change = useCallback(
    (text: FreeDrawText) => {
      setFreeDrawText({
        ...freeDrawText,
        content: text.content,
        color: text.color,
        font_size: text.font_size,
        timestamp: { ...freeDrawText.timestamp, update_date: text.timestamp.update_date },
      });
    },
    [freeDrawText]
  );

  const changeCoord = useCallback(
    (text: FreeDrawText) => {
      setFreeDrawText({
        ...freeDrawText,
        x: text.x,
        y: text.y,
        timestamp: { ...freeDrawText.timestamp, update_date: text.timestamp.update_date },
      });
    },
    [freeDrawText]
  );

  const endDrag = (rect: Rectangle) => {
    setRect(rect);
  };

  const centerX = useCallback((): number => {
    if (!rect) return 0;

    return rect.x + rect.width / 2;
  }, [rect]);

  return { freeDrawText, isEditable, start, end, change, changeCoord, endDrag, centerX };
};
